import React from 'react'
import styled from '@emotion/styled'

import {Button} from '@vio/atlas/Button'
import {ButtonGroup} from '@vio/atlas/ButtonGroup'
import {Stack} from '@vio/atlas/helpers/Stack'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {DatePickerType} from '@vio/core/src/datePicker/types'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'

import {FakeDatePickerInput} from './FakeDatePickerInput'
import {FakeDestinationInput} from './FakeDestinationInput'
import {FakeGuestPickerInput} from './FakeGuestPickerInput'

interface BaseProps {
  /** Check in date */
  checkIn?: string
  /** Check out date */
  checkOut?: string
  /** Rooms configuration */
  numberOfAdults: number
  /** Rooms configuration */
  numberOfChildren: number
  /** Rooms configuration */
  numberOfRooms: number
  /** Callback for Datepicker click */
  onDatePickerClick: (type: DatePickerType) => void
  /** Callback for Guestpicker click */
  onGuestPickerClick: () => void
  /** Callback for Submit click */
  onSubmit: () => void
  /** Whether the rooms count and label is shown */
  isRoomsLabelShown?: boolean
}

interface PropsWithDestination extends BaseProps {
  /** Destination name */
  destination: string
  /** Callback for Destinationpicker click */
  onDestinationPickerClick: () => void
  /** Destination error flag */
  destinationHasError?: boolean
  /** Destination error message */
  destinationErrorMessage?: {
    id: string
    defaultMessage: string
  }
}

interface PropsWithoutDestination extends BaseProps {
  showDestination?: never
  destination?: never
  onDestinationPickerClick?: never
  destinationHasError?: never
  destinationErrorMessage?: never
}

type Props = PropsWithDestination | PropsWithoutDestination

const InputError = styled(Text)`
  padding-block: ${cssTheme.layout.spacing.s300} 0;
  padding-inline: ${cssTheme.layout.spacing.s500} 0;
`

export const SearchFormVertical = React.memo(function SearchFormVertical({
  checkIn,
  checkOut,
  destination,
  numberOfAdults,
  numberOfChildren,
  numberOfRooms,
  onDestinationPickerClick,
  onDatePickerClick,
  onGuestPickerClick,
  onSubmit,
  destinationHasError = false,
  destinationErrorMessage,
  isRoomsLabelShown = true
}: Props) {
  const {shell} = useBrandCapabilities()
  const handleDatePickerClick = (type: DatePickerType) => {
    // Only open checkOut when there is a checkIn set
    const openType = checkIn ? type : DatePickerType.CheckIn
    onDatePickerClick(openType)
  }

  return (
    <Stack space="s300">
      {onDestinationPickerClick && (
        <>
          <FakeDestinationInput
            value={destination}
            hasError={destinationHasError}
            onClick={onDestinationPickerClick}
          />
          {destinationHasError && destinationErrorMessage && (
            <InputError colorPath="content.danger.secondary" variant="bodyXS">
              <FormattedMessageWrapper {...destinationErrorMessage} />
            </InputError>
          )}
        </>
      )}

      <ButtonGroup>
        <FakeDatePickerInput
          value={checkIn}
          type={DatePickerType.CheckIn}
          onClick={() => handleDatePickerClick(DatePickerType.CheckIn)}
        />
        <FakeDatePickerInput
          value={checkOut}
          type={DatePickerType.CheckOut}
          onClick={() => handleDatePickerClick(DatePickerType.CheckOut)}
        />
      </ButtonGroup>

      <div>
        <FakeGuestPickerInput
          numberOfAdults={numberOfAdults}
          numberOfChildren={numberOfChildren}
          numberOfRooms={numberOfRooms}
          onClick={onGuestPickerClick}
          isRoomsLabelShown={isRoomsLabelShown}
        />
      </div>

      <div>
        <Button
          variant="primary"
          fullWidth
          type="submit"
          dataId="SearchButton"
          onClick={onSubmit}
          iconStart={<Icon name="Search" />}
          isRounded={shell.hasRoundedSearchSubmitButton}
        >
          <FormattedMessageWrapper id="search" defaultMessage="Search" />
        </Button>
      </div>
    </Stack>
  )
})
